import { useState } from 'react';
import { AxiosError } from 'axios';
import type { AdminUserListItem, ApiResponse, UserRole } from '@/types/api';
import { Dialog } from '@/components/ui/dialog';
import { useAuth } from '@/features/auth/hooks/use-auth';
import {
  ConflictError,
  updateUser,
  type UpdateUserPayload,
} from '../api/admin-users-api';

type Phase = 'form' | 'submitting';

interface FieldErrors {
  firstName?: string;
  lastName?: string;
  role?: string;
}

interface EditUserDialogProps {
  open: boolean;
  user: AdminUserListItem;
  currentAdminId: number;
  /** Called after the server returns 200 (and we're NOT force-logging-out). */
  onSaved: () => void;
  /** Called when the admin dismisses the dialog without saving. */
  onClose: () => void;
}

const ROLE_OPTIONS: UserRole[] = ['User', 'Admin'];

function parseServerErrors(body: ApiResponse<unknown> | undefined): {
  fields: FieldErrors;
  banner?: string;
} {
  if (!body) return { fields: {}, banner: undefined };
  const fields: FieldErrors = {};
  let banner: string | undefined;

  const lines = body.errors && body.errors.length > 0 ? body.errors : body.message ? [body.message] : [];
  for (const line of lines) {
    const lower = line.toLowerCase();
    if (lower.includes('first')) fields.firstName = fields.firstName ?? line;
    else if (lower.includes('last')) fields.lastName = fields.lastName ?? line;
    else if (lower.includes('role')) fields.role = fields.role ?? line;
    else banner = banner ?? line;
  }
  return { fields, banner };
}

export function EditUserDialog({
  open,
  user,
  currentAdminId,
  onSaved,
  onClose,
}: EditUserDialogProps) {
  const { logout } = useAuth();

  const [phase, setPhase] = useState<Phase>('form');
  const [firstName, setFirstName] = useState(user.firstName);
  const [lastName, setLastName] = useState(user.lastName);
  const [role, setRole] = useState<UserRole>(user.role);
  const [isActive, setIsActive] = useState<boolean>(user.isActive);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [bannerError, setBannerError] = useState<string | null>(null);
  const [conflictError, setConflictError] = useState<string | null>(null);

  // NOTE: this component is keyed on `user.id` by its parent, so each new
  // edit target gets a fresh component instance — local form state is seeded
  // from the `user` prop via useState's lazy initializers above (no syncing
  // effect needed).

  const isSelf = user.id === currentAdminId;

  const handleClose = () => {
    if (phase === 'submitting') return;
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFieldErrors({});
    setBannerError(null);
    setConflictError(null);

    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();

    const localErrors: FieldErrors = {};
    if (!trimmedFirst) localErrors.firstName = 'First name is required.';
    if (!trimmedLast) localErrors.lastName = 'Last name is required.';
    if (Object.keys(localErrors).length > 0) {
      setFieldErrors(localErrors);
      return;
    }

    const payload: UpdateUserPayload = {
      firstName: trimmedFirst,
      lastName: trimmedLast,
      role,
      isActive,
    };

    setPhase('submitting');
    try {
      await updateUser(user.id, payload);

      // Self-deactivation: terminate the session client-side. The app's auth
      // context clearing + protected-route guard will route to /login.
      if (isSelf && isActive === false) {
        logout();
        return;
      }

      onSaved();
      onClose();
    } catch (err) {
      if (err instanceof ConflictError) {
        setConflictError(err.message);
        setPhase('form');
        return;
      }
      if (err instanceof AxiosError && err.response?.status === 400) {
        const parsed = parseServerErrors(err.response.data as ApiResponse<unknown> | undefined);
        setFieldErrors(parsed.fields);
        setBannerError(parsed.banner ?? null);
        setPhase('form');
        return;
      }
      setBannerError('Could not update user. Check API connectivity and try again.');
      setPhase('form');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} title="Edit user" dismissible={phase !== 'submitting'}>
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="edit-user-form" noValidate>
        {isSelf && (
          <div
            role="alert"
            data-testid="edit-user-self-banner"
            className="rounded border border-yellow-500/60 bg-yellow-500/10 px-3 py-2 text-sm text-yellow-200"
          >
            You are modifying your own account. You may lose admin access.
          </div>
        )}

        {conflictError && (
          <div
            role="alert"
            data-testid="edit-user-conflict-error"
            className="rounded border border-red-500 bg-red-500/10 px-3 py-2 text-sm text-red-300"
          >
            {conflictError}
          </div>
        )}

        {bannerError && (
          <div
            role="alert"
            data-testid="edit-user-banner-error"
            className="rounded border border-red-500 bg-red-500/10 px-3 py-2 text-sm text-red-300"
          >
            {bannerError}
          </div>
        )}

        <Field
          id="edit-user-email"
          label="Email"
          input={
            <input
              id="edit-user-email"
              data-testid="edit-user-email-input"
              type="email"
              readOnly
              value={user.email}
              className="w-full cursor-not-allowed rounded border border-gray-700 bg-gray-900/60 px-3 py-2 text-sm text-gray-400"
            />
          }
        />
        <Field
          id="edit-user-first-name"
          label="First name"
          error={fieldErrors.firstName}
          input={
            <input
              id="edit-user-first-name"
              data-testid="edit-user-first-name-input"
              type="text"
              required
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full rounded border border-gray-600 bg-gray-900 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none"
            />
          }
        />
        <Field
          id="edit-user-last-name"
          label="Last name"
          error={fieldErrors.lastName}
          input={
            <input
              id="edit-user-last-name"
              data-testid="edit-user-last-name-input"
              type="text"
              required
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full rounded border border-gray-600 bg-gray-900 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none"
            />
          }
        />
        <Field
          id="edit-user-role"
          label="Role"
          error={fieldErrors.role}
          input={
            <select
              id="edit-user-role"
              data-testid="edit-user-role-select"
              value={role}
              onChange={(e) => setRole(e.target.value as UserRole)}
              className="w-full rounded border border-gray-600 bg-gray-900 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none"
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          }
        />

        <div className="flex items-center gap-2">
          <input
            id="edit-user-is-active"
            data-testid="edit-user-is-active-checkbox"
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="h-4 w-4 rounded border-gray-600 bg-gray-900 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="edit-user-is-active" className="text-sm text-gray-200">
            Active
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            data-testid="edit-user-cancel-button"
            onClick={handleClose}
            disabled={phase === 'submitting'}
            className="rounded border border-gray-600 px-4 py-2 text-sm text-gray-200 hover:bg-gray-700 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid="edit-user-submit-button"
            disabled={phase === 'submitting'}
            className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
          >
            {phase === 'submitting' ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

interface FieldProps {
  id: string;
  label: string;
  error?: string;
  input: React.ReactNode;
}

function Field({ id, label, error, input }: FieldProps) {
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-gray-200">
        {label}
      </label>
      {input}
      {error && (
        <p data-testid={`${id}-error`} role="alert" className="mt-1 text-xs text-red-400">
          {error}
        </p>
      )}
    </div>
  );
}
